/**
 * Audit UI Slice
 *
 * Redux slice for managing PURE UI state only.
 * Does NOT duplicate RTK Query server state.
 * Computed values should be derived via hooks/selectors from RTK Query cache.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AuditCardStatus } from '../components/cards';

// ============================================================================
// Types
// ============================================================================

export type SidebarMode = 'chat' | 'audit' | 'analysis';

export type CardCompletedVia = 'ai' | 'manual';

export interface CardState {
  status: AuditCardStatus;
  affectedSectionIds: string[];
  completedVia?: CardCompletedVia;
  graduated?: boolean;
  changeSummary?: string;
}

export interface AuditUiState {
  // Pure UI state only - no server state duplication
  highlightedSectionId: string | null; // Section ID to highlight rules for
  sidebarMode: SidebarMode; // Current sidebar mode
  dismissedPayerBannerNoteIds: string[]; // Note IDs where user dismissed the payer-aware banner (session only)
  dismissedAddTemplateBannerNoteIds: string[]; // Note IDs where user dismissed the "add template" banner (session only)

  // Card lifecycle state (session-scoped, not persisted)
  cardStates: Record<string, CardState>; // ruleId → status + affected sections
  updatingRuleId: string | null; // Which card triggered the current AI edit
  preFixSectionIds: string[]; // Snapshot of affectedSectionIds before AI fix (for diffing)
  preFixSnapshotKeys: string[]; // Snapshot of sectionPreviousContent keys before AI fix
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuditUiState = {
  highlightedSectionId: null,
  sidebarMode: 'chat',
  dismissedPayerBannerNoteIds: [],
  dismissedAddTemplateBannerNoteIds: [],
  cardStates: {},
  updatingRuleId: null,
  preFixSectionIds: [],
  preFixSnapshotKeys: [],
};

// ============================================================================
// Slice
// ============================================================================

export const auditUiSlice = createSlice({
  name: 'jotaudit/ui',
  initialState,
  reducers: {
    /**
     * Set the highlighted section (for scrolling/highlighting in audit sidebar)
     */
    setHighlightedSection: (state, action: PayloadAction<string>) => {
      state.highlightedSectionId = action.payload;
    },

    /**
     * Clear the highlighted section
     */
    clearHighlightedSection: state => {
      state.highlightedSectionId = null;
    },

    /**
     * Set the sidebar mode
     */
    setSidebarMode: (state, action: PayloadAction<SidebarMode>) => {
      state.sidebarMode = action.payload;
    },

    /**
     * Open audit sidebar and highlight a specific section's rules
     */
    openAuditForSection: (state, action: PayloadAction<string>) => {
      state.sidebarMode = 'audit';
      state.highlightedSectionId = action.payload;
    },

    /**
     * Dismiss the payer-aware template banner for a specific note (session-level, not persisted)
     */
    dismissPayerBannerForNote: (state, action: PayloadAction<string>) => {
      if (!state.dismissedPayerBannerNoteIds.includes(action.payload)) {
        state.dismissedPayerBannerNoteIds.push(action.payload);
      }
    },

    /**
     * Dismiss the "add template to list" banner for a specific note (session-level, not persisted)
     */
    dismissAddTemplateBannerForNote: (state, action: PayloadAction<string>) => {
      if (!state.dismissedAddTemplateBannerNoteIds.includes(action.payload)) {
        state.dismissedAddTemplateBannerNoteIds.push(action.payload);
      }
    },

    /**
     * Reset UI state (e.g., when navigating away)
     */
    resetAuditUi: state => {
      state.highlightedSectionId = null;
      state.cardStates = {};
      state.updatingRuleId = null;
      state.preFixSectionIds = [];
      state.preFixSnapshotKeys = [];
      // Note: we don't reset sidebarMode or dismissed banner note IDs as user preferences should persist
    },

    // ---- Card lifecycle actions ----

    /**
     * Begin an AI fix for a rule card.
     * Stores the pre-fix affected section IDs so the middleware can diff later.
     */
    startCardUpdate: (
      state,
      action: PayloadAction<{
        ruleId: string;
        preFixSectionIds: string[];
        preFixSnapshotKeys: string[];
      }>,
    ) => {
      const { ruleId, preFixSectionIds, preFixSnapshotKeys } = action.payload;

      // Graduate all completed/ignored non-graduated cards
      for (const [id, card] of Object.entries(state.cardStates)) {
        if ((card.status === 'completed' || card.status === 'ignored') && !card.graduated) {
          state.cardStates[id].graduated = true;
        }
      }

      state.cardStates[ruleId] = { status: 'updating', affectedSectionIds: [] };
      state.updatingRuleId = ruleId;
      state.preFixSectionIds = preFixSectionIds;
      state.preFixSnapshotKeys = preFixSnapshotKeys;
    },

    /**
     * AI fix completed successfully. Called by cardCompletionMiddleware.
     */
    completeCardUpdate: (
      state,
      action: PayloadAction<{ affectedSectionIds: string[]; changeSummary?: string }>,
    ) => {
      const ruleId = state.updatingRuleId;
      if (ruleId) {
        state.cardStates[ruleId] = {
          status: 'completed',
          affectedSectionIds: action.payload.affectedSectionIds,
          completedVia: 'ai',
          graduated: false,
          changeSummary: action.payload.changeSummary,
        };
      }
      state.updatingRuleId = null;
      state.preFixSectionIds = [];
      state.preFixSnapshotKeys = [];
    },

    /**
     * Direct completion (e.g., user manually resolved the issue).
     * Bypasses the middleware flow — no AI message sent.
     */
    setCardCompleted: (
      state,
      action: PayloadAction<{
        ruleId: string;
        affectedSectionIds: string[];
        completedVia: CardCompletedVia;
      }>,
    ) => {
      // Graduate all existing completed/ignored non-graduated cards
      for (const [id, card] of Object.entries(state.cardStates)) {
        if ((card.status === 'completed' || card.status === 'ignored') && !card.graduated) {
          state.cardStates[id].graduated = true;
        }
      }
      const { ruleId, affectedSectionIds, completedVia } = action.payload;
      state.cardStates[ruleId] = { status: 'completed', affectedSectionIds, completedVia, graduated: false };
    },

    /**
     * AI fix failed (error). Called by cardCompletionMiddleware.
     */
    failCardUpdate: state => {
      const ruleId = state.updatingRuleId;
      if (ruleId) {
        const { [ruleId]: _removed, ...rest } = state.cardStates;
        state.cardStates = rest;
      }
      state.updatingRuleId = null;
      state.preFixSectionIds = [];
      state.preFixSnapshotKeys = [];
    },

    /**
     * User dismissed/ignored a rule card.
     */
    setCardIgnored: (state, action: PayloadAction<string>) => {
      // Graduate all existing completed/ignored non-graduated cards
      for (const [id, card] of Object.entries(state.cardStates)) {
        if ((card.status === 'completed' || card.status === 'ignored') && !card.graduated) {
          state.cardStates[id].graduated = true;
        }
      }
      state.cardStates[action.payload] = { status: 'ignored', affectedSectionIds: [], graduated: false };
    },

    /**
     * AI interrupt arrived during an updating card. Card needs user input.
     */
    setCardInterrupted: (state, action: PayloadAction<string>) => {
      const ruleId = action.payload;
      if (state.cardStates[ruleId]?.status === 'updating') {
        state.cardStates[ruleId].status = 'interrupted';
      }
    },

    /**
     * User responded to interrupt — card resumes updating.
     */
    resumeCardUpdate: (state, action: PayloadAction<string>) => {
      const ruleId = action.payload;
      if (state.cardStates[ruleId]?.status === 'interrupted') {
        state.cardStates[ruleId].status = 'updating';
      }
    },

    /**
     * Undo: revert card back to idle (delete from cardStates).
     */
    resetCardState: (state, action: PayloadAction<string>) => {
      const { [action.payload]: _removed, ...rest } = state.cardStates;
      state.cardStates = rest;
    },

    /**
     * Clear all card states (e.g., new audit arrived).
     */
    resetAllCardStates: state => {
      state.cardStates = {};
      state.updatingRuleId = null;
      state.preFixSectionIds = [];
      state.preFixSnapshotKeys = [];
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setHighlightedSection,
  clearHighlightedSection,
  setSidebarMode,
  openAuditForSection,
  dismissPayerBannerForNote,
  dismissAddTemplateBannerForNote,
  resetAuditUi,
  startCardUpdate,
  completeCardUpdate,
  setCardCompleted,
  failCardUpdate,
  setCardIgnored,
  setCardInterrupted,
  resumeCardUpdate,
  resetCardState,
  resetAllCardStates,
} = auditUiSlice.actions;

export default auditUiSlice.reducer;
